import { useState } from 'react';
import { Loader2, Plus, SquarePen } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '~shared/shadcn/ui/button';
import { Switch } from '~shared/shadcn/ui/switch';
import { getErrorMessage } from '~shared/utils/error-handler';
import { useGetFormConfigs } from '~features/form-submissions/api/get-form-configs';
import { formatFormTypeLabel } from '~features/form-submissions/lib/form-type-labels';
import type { StudioFormConfigSummary } from '~features/form-submissions/api/types';
import { useUpdateFormConfig } from '../api/form-config-actions';
import { AddFormDialog } from './add-form-dialog';
import { FormConfigEditor } from './form-config-editor';

export function FormsSettingsPage() {
  const { data: configs, isLoading, isError } = useGetFormConfigs();
  const updateMutation = useUpdateFormConfig();
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<StudioFormConfigSummary | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const activeCount = (configs ?? []).filter((c) => c.isActive).length;

  const openEditor = (config: StudioFormConfigSummary) => {
    setEditing(config);
    setEditorOpen(true);
  };

  const handleToggleActive = (
    config: StudioFormConfigSummary,
    nextActive: boolean,
  ) => {
    setTogglingId(config._id);
    updateMutation.mutate(
      {
        id: config._id,
        body: { isActive: nextActive },
      },
      {
        onSuccess: () => {
          toast.success(
            nextActive
              ? `"${config.formName}" is now active`
              : `"${config.formName}" is now inactive`,
          );
        },
        onError: (mutationError) => {
          toast.error(
            getErrorMessage(mutationError, 'Could not update form status'),
          );
        },
        onSettled: () => {
          setTogglingId(null);
        },
      },
    );
  };

  return (
    <div className='flex flex-col gap-4 p-4 md:p-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-xl font-semibold'>Forms</h1>
          <p className='text-sm text-muted-foreground'>
            Embeddable lead forms for your website.
            {activeCount > 0 ? ` ${activeCount} active.` : null}
          </p>
        </div>
        <Button onClick={() => { setAddOpen(true); }}>
          <Plus className='mr-2 size-4' />
          Add a form
        </Button>
      </div>

      {isLoading ? (
        <div className='flex items-center justify-center py-16'>
          <Loader2 className='size-6 animate-spin text-muted-foreground' />
        </div>
      ) : isError ? (
        <p className='rounded-md bg-red-50 px-3 py-2 text-sm text-red-700'>
          Could not load forms. Refresh and try again.
        </p>
      ) : !configs?.length ? (
        <div className='rounded-lg border border-dashed py-16 text-center'>
          <p className='text-sm font-medium'>No forms yet</p>
          <p className='mt-1 text-sm text-muted-foreground'>
            Add a form to start capturing leads from your website.
          </p>
          <Button
            variant='outline'
            className='mt-4'
            onClick={() => { setAddOpen(true); }}
          >
            <Plus className='mr-2 size-4' />
            Add a form
          </Button>
        </div>
      ) : (
        <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
          {configs.map((config) => (
            <div
              key={config._id}
              className='flex flex-col gap-3 rounded-lg border bg-white p-4'
            >
              <div className='flex items-start justify-between gap-2'>
                <div className='min-w-0'>
                  <p className='truncate text-sm font-medium'>
                    {config.formName}
                  </p>
                  <p className='text-xs text-muted-foreground'>
                    {formatFormTypeLabel(config.formType)}
                  </p>
                </div>
                <span
                  className='size-4 shrink-0 rounded-full border'
                  style={{
                    backgroundColor: config.theme?.primaryColor ?? '#e5e7eb',
                  }}
                  title='Primary color'
                />
              </div>
              <div className='flex items-center justify-between gap-2'>
                <div className='flex items-center gap-2'>
                  <Switch
                    checked={config.isActive}
                    disabled={togglingId === config._id}
                    onCheckedChange={(checked) =>
                      { handleToggleActive(config, checked); }
                    }
                    aria-label={`Toggle ${config.formName} active`}
                  />
                  <span className='text-xs text-muted-foreground'>
                    {config.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => { openEditor(config); }}
                >
                  <SquarePen className='mr-1.5 size-3.5' />
                  Edit
                </Button>
              </div>
              <p className='text-xs text-muted-foreground'>
                {config.portalSyncEnabled ? 'Portal on' : 'Portal off'}
                {' · '}
                {config.ghlSyncEnabled ? 'CRM on' : 'CRM off'}
              </p>
            </div>
          ))}
        </div>
      )}

      <AddFormDialog open={addOpen} onOpenChange={setAddOpen} />

      <FormConfigEditor
        config={editing}
        studioId={editing?.studioId ?? ''}
        studioName={editing?.studioName}
        open={editorOpen}
        onOpenChange={(open) => {
          setEditorOpen(open);
          if (!open) setEditing(null);
        }}
      />
    </div>
  );
}
